import React, { useState, useEffect } from 'react';
import { useDbUser } from '../hooks/useDbUser';
import { db, sql } from '../lib/db';
import { AlertTriangle, CheckCircle, Wrench, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

interface BrokenDependency {
  task_id: string;
  task_title: string;
  goal_id: string;
  goal_title: string;
  depends_on: string[] | null;
  offset_from_id: string | null;
  offset_type: string;
  parent_task_id: string | null;
  parent_task_title: string | null;
  parent_status: string | null;
  issue: string;
}

export function DependencyFixPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const [loading, setLoading] = useState(true);
  const [brokenDependencies, setBrokenDependencies] = useState<BrokenDependency[]>([]);
  const [fixing, setFixing] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      checkBrokenDependencies();
    }
  }, [dbUserId, dbUserLoading]);


  const checkBrokenDependencies = async () => {
    if (!dbUserId) return;

    setLoading(true);
    try {
      // Find tasks that should be live based on their dependencies
      const result = await sql`
        WITH broken_tasks AS (
          -- Find tasks with depends_on where all dependencies are complete
          SELECT 
            t.id as task_id,
            t.title as task_title,
            t.goal_id,
            g.title as goal_title,
            t.depends_on,
            t.offset_from_id,
            t.offset_type,
            NULL as parent_task_id,
            NULL as parent_task_title,
            NULL as parent_status,
            'All dependencies complete but task not live' as issue
          FROM tasks t
          LEFT JOIN goals g ON t.goal_id = g.id
          WHERE t.user_id = ${dbUserId}
          AND t.status != 'Complete'
          AND t.status != 'Superseded'
          AND t.is_live = 0
          AND EXISTS (SELECT 1 FROM task_dependencies td WHERE td.task_id = t.id)
          AND NOT EXISTS (
            SELECT 1 
            FROM task_dependencies td
            JOIN tasks dep ON dep.id = td.depends_on_task_id
            WHERE td.task_id = t.id AND (dep.status IS NULL OR dep.status != 'Complete')
          )
          
          UNION ALL
          
          -- Find offset tasks with days_from_completion where parent is complete
          SELECT 
            t.id as task_id,
            t.title as task_title,
            t.goal_id,
            g.title as goal_title,
            t.depends_on,
            t.offset_from_id,
            t.offset_type,
            p.id as parent_task_id,
            p.title as parent_task_title,
            p.status as parent_status,
            'Parent task complete but offset task not live' as issue
          FROM tasks t
          LEFT JOIN goals g ON t.goal_id = g.id
          LEFT JOIN tasks p ON t.offset_from_id = p.id
          WHERE t.user_id = ${dbUserId}
          AND t.status != 'Complete'
          AND t.status != 'Superseded'
          AND t.is_live = false
          AND t.offset_type = 'days_from_completion'
          AND t.offset_from_id IS NOT NULL
          AND p.status = 'Complete'
          
          UNION ALL
          
          -- Find tasks that are live but shouldn't be
          SELECT 
            t.id as task_id,
            t.title as task_title,
            t.goal_id,
            g.title as goal_title,
            t.depends_on,
            t.offset_from_id,
            t.offset_type,
            NULL as parent_task_id,
            NULL as parent_task_title,
            NULL as parent_status,
            'Task is live but has incomplete dependencies' as issue
          FROM tasks t
          LEFT JOIN goals g ON t.goal_id = g.id
          WHERE t.user_id = ${dbUserId}
          AND t.status != 'Complete'
          AND t.status != 'Superseded'
          AND t.is_live = 1
          AND (
            -- Has incomplete depends_on dependencies
            (EXISTS (
               SELECT 1 
               FROM task_dependencies td
               JOIN tasks dep ON dep.id = td.depends_on_task_id
               WHERE td.task_id = t.id AND (dep.status IS NULL OR dep.status != 'Complete')
             ))
            OR
            -- Has incomplete offset parent
            (t.offset_type = 'days_from_completion'
             AND t.offset_from_id IS NOT NULL
             AND EXISTS (
               SELECT 1 
               FROM tasks p
               WHERE p.id = t.offset_from_id
               AND p.status != 'Complete'
             ))
          )
        )
        SELECT * FROM broken_tasks
        ORDER BY goal_title, task_title
      `;
      
      setBrokenDependencies(result);
    } catch (error) {
      console.error('Error checking broken dependencies:', error);
      toast.error('Failed to check for broken dependencies');
    } finally {
      setLoading(false);
    }
  };

  const fixBrokenDependencies = async () => {
    if (!dbUserId || brokenDependencies.length === 0) return;

    setFixing(true);
    try {
      // Fix tasks that should be live
      const shouldBeLive = brokenDependencies.filter(d => 
        d.issue.includes('not live')
      );
      
      if (shouldBeLive.length > 0) {
        const taskIds = shouldBeLive.map(d => d.task_id);
        const placeholders = taskIds.map((_, i) => `$${i + 1}`).join(', ');
        await db.sqlRaw(
          `UPDATE tasks SET is_live = 1 WHERE id IN (${placeholders}) AND user_id = $${taskIds.length + 1}`,
          [...taskIds, dbUserId]
        );
      }

      // Fix tasks that shouldn't be live
      const shouldNotBeLive = brokenDependencies.filter(d => 
        d.issue.includes('is live but')
      );
      
      if (shouldNotBeLive.length > 0) {
        const taskIds = shouldNotBeLive.map(d => d.task_id);
        const placeholders = taskIds.map((_, i) => `$${i + 1}`).join(', ');
        await db.sqlRaw(
          `UPDATE tasks SET is_live = 0 WHERE id IN (${placeholders}) AND user_id = $${taskIds.length + 1}`,
          [...taskIds, dbUserId]
        );
      }

      toast.success(`Fixed ${brokenDependencies.length} task dependencies`);
      
      // Refresh the list
      await checkBrokenDependencies();
    } catch (error) {
      console.error('Error fixing dependencies:', error);
      toast.error('Failed to fix dependencies');
    } finally {
      setFixing(false);
    }
  };

  const toggleExpanded = (taskId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedItems(newExpanded);
  };

  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Checking task dependencies...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Task Dependency Fix Tool</h1>
        <p className="text-gray-600">
          Identify and fix tasks with broken dependency relationships
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {brokenDependencies.length > 0 ? (
              <>
                <AlertTriangle className="w-6 h-6 text-amber-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Found {brokenDependencies.length} tasks with dependency issues
                </h2>
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">
                  All task dependencies are working correctly!
                </h2>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={checkBrokenDependencies}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            {brokenDependencies.length > 0 && (
              <button
                onClick={fixBrokenDependencies}
                disabled={fixing}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-300"
              >
                <Wrench className="w-4 h-4 mr-2" />
                {fixing ? 'Fixing...' : 'Fix All Issues'}
              </button>
            )}
          </div>
        </div>

        {brokenDependencies.length > 0 && (
          <div className="space-y-3">
            {brokenDependencies.map((dep) => (
              <div
                key={dep.task_id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleExpanded(dep.task_id)}
                        className="mr-2 text-gray-500 hover:text-gray-700"
                      >
                        {expandedItems.has(dep.task_id) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <h3 className="font-medium text-gray-900">{dep.task_title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-6">
                      Deliverable: {dep.goal_title || 'None'}
                    </p>
                    <p className="text-sm text-amber-600 mt-1 ml-6">
                      Issue: {dep.issue}
                    </p>
                    
                    {expandedItems.has(dep.task_id) && (
                      <div className="mt-3 ml-6 text-sm text-gray-600 space-y-1">
                        {dep.depends_on && dep.depends_on.length > 0 && (
                          <p>Dependencies: {dep.depends_on.length} task(s)</p>
                        )}
                        {dep.parent_task_id && (
                          <p>
                            Parent: {dep.parent_task_title} 
                            <span className="text-gray-500"> (Status: {dep.parent_status})</span>
                          </p>
                        )}
                        {dep.offset_type && dep.offset_type !== 'none' && (
                          <p>Offset Type: {dep.offset_type}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
